<template>
    <default-aside @okHandler="onSubmit" :title="target.title">
        <b-form id="form-target-evaluation" novalidate>
            <b-form-group :label="$t('targetEvaluation.rangeLabel')" v-if="ranges.length">
                <b-form-radio-group
                    v-model="selectedRange"
                    :options="options"
                    buttons
                    stacked
                    button-variant="outline-primary"
                    @change="isChangedFromOptions = true"
                ></b-form-radio-group>
            </b-form-group>
            <c-input
                id="target"
                :label="`${$t('targets.performance')}*`"
                :placeholder="$t('form.placeholder.performance')"
                :value.sync="$v.form.target.$model"
                :state="checkState('target')"
                labelClass="font-weight-bold"
                @focus="isChangedFromOptions = false"
            >
                <span v-if="!$v.form.target.required">
                    {{ $t('form.validations.required') }}
                </span>
                <span v-if="!$v.form.target.decimal">
                    {{ $t('form.validations.decimal') }}
                </span>
            </c-input>

            <b-form-textarea
                :placeholder="$t('targetEvaluation.commentPlaceholder')"
                rows="5"
                max-rows="8"
                v-model="commentText"
            ></b-form-textarea>
        </b-form>
        <template slot="okButton">
            <i v-if="isSubmitting" class="fa fa-spinner fa-spin px-2" />
        </template>
    </default-aside>
</template>

<script>
import DefaultAside from '@/containers/DefaultAside'
import { unionBy, escape, sortBy } from 'lodash'
import { getRangesFromAPI, getGoalRangesFromAPI, evaluateGoal } from './api'
import CInput from './components/fields'
import { validationMixin } from 'vuelidate'
import i18n from '@/i18n'
import { decimal, required } from 'vuelidate/lib/validators'
import notification, { success, danger } from '@/lib/notification'

notification.init()

export default {
    name: 'TargetEvaluationAside',
    props: ['target', 'type', 'evaluation'],
    components: {
        DefaultAside,
        CInput,
    },
    mixins: [validationMixin],
    validations: {
        form: {
            target: {
                required,
                decimal,
            },
        },
    },
    data() {
        return {
            selectedRange: null,
            commentText: null,
            isSubmitting: false,
            isChangedFromOptions: false,
            submitted: false,
            ranges: [],
            form: {
                target: null,
            },
        }
    },
    mounted() {
        if (this.evaluation) this.setAside()
        this.getAndMergeRanges(this.target)
    },
    methods: {
        async getAndMergeRanges(target) {
            const ranges = await getRangesFromAPI()
            const goalRanges = await getGoalRangesFromAPI(target.id)
            this.ranges = unionBy(ranges, goalRanges || [], 'id')
            this.selectedRange = this.evaluation !== undefined ? this.getRangeEvaluated() : null
        },
        getRangeEvaluated() {
            const countRanges = this.rangesOrderByDesc.length
            let currentRange = this.rangesOrderByDesc[countRanges - 1]
            this.ranges.forEach((range) => {
                if (range.id == this.evaluation.range_id) {
                    currentRange = range
                }
            })

            return currentRange
        },
        checkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        onSubmit(closeAside) {
            this.submitted = true

            if (this.validate()) {
                return
            }

            this.isSubmitting = true

            evaluateGoal(this.target.id, {
                type: this.type,
                range_id: this.selectedRange ? this.selectedRange.id : null,
                current_value: this.form.target,
                comments: this.commentText,
            })
                .then((result) => {
                    this.notificationTargetDeletedSuccessfully()

                    closeAside(result.data.data)
                })
                .finally(() => {
                    this.isSubmitting = false
                    this.submitted = false
                })
        },
        validate() {
            this.$v.$touch()

            return this.$v.$invalid
        },
        notificationTargetDeletedSuccessfully() {
            success(
                i18n.t('notification.requestSentSuccessfully', {
                    item: i18n.t('targets.target'),
                })
            )
        },
        notificationError() {
            danger()
        },
        setAside() {
            this.selectedRange = this.evaluation.range_id
            this.form.target = this.evaluation.current_value
            this.commentText = this.evaluation.comments
        },
    },
    computed: {
        options() {
            return this.ranges.map((range) => ({
                html: `
                    <div class="text-left text-justify w-100">
                        <div class="font-weight-bold">
                            ${escape(range.title)}
                        </div>
                        <small>${escape(range.description || '')}</small>
                    </div>`,
                value: range,
            }))
        },
        rangesOrderByDesc() {
            return sortBy(this.ranges, ['target'], ['desc']).reverse()
        },
    },
    watch: {
        target(newTarget) {
            this.getAndMergeRanges(newTarget)
        },
        selectedRange(newValue) {
            if (this.isChangedFromOptions) {
                this.$v.form.target.$model = newValue.target
            }
        },
        'form.target'(newValue) {
            const countRanges = this.rangesOrderByDesc.length
            let range = this.rangesOrderByDesc[countRanges - 1]
            for (let index = 0; index < countRanges; index++) {
                const target = this.rangesOrderByDesc[index].target
                if (newValue >= target) {
                    range = this.rangesOrderByDesc[index]
                    break
                }
            }
            this.selectedRange = range
        },
    },
}
</script>
